import { EVENT_TYPE_ALIASES } from "./constants";

/**
 * Minimal CSV parser (handles quoted fields and CRLF). The safety event files
 * are small and have a fixed layout so a dependency is not warranted.
 * @param {String} text CSV contents
 * @return {Array<Object>} one object per row keyed by header
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === "\"") {
        if (text[i + 1] === "\"") {
          field += "\"";
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === "\"") {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") {
        i++;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }

  const [header, ...body] = rows.filter((r) => r.length > 1 || (r.length === 1 && r[0] !== ""));
  if (!header) {
    return [];
  }
  const keys = header.map((k) => k.trim());
  return body.map((values) => Object.fromEntries(keys.map((key, idx) => [key, (values[idx] ?? "").trim()])));
}

export function normalizeEventType(raw) {
  const key = String(raw ?? "").trim().toLowerCase();
  return EVENT_TYPE_ALIASES[key] || key.replace(/\s+/g, "_") || "unknown";
}

const TIME_PATTERN = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2}):(\d{2})$/;

/**
 * Parse the `dd-MM-yyyy HH:mm:ss` timestamps. They are already in the local
 * time of the study area, so hour-of-day is read straight from the string.
 */
export function parseEventTime(raw) {
  const match = TIME_PATTERN.exec(String(raw ?? "").trim());
  if (!match) {
    return { endTime: null, hour: null };
  }
  const [, dd, MM, yyyy, HH, mm, ss] = match;
  return {
    endTime: `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}`,
    hour: Number(HH)
  };
}

function toNumber(value) {
  const n = Number(value);
  return value !== "" && value !== null && value !== undefined && Number.isFinite(n) ? n : null;
}

/**
 * @param {Object} row raw CSV row
 * @return {Object|null} normalized event or null when it has no usable location
 */
export function normalizeEvent(row) {
  const lon = toNumber(row.lon ?? row.longitude);
  const lat = toNumber(row.lat ?? row.latitude);
  if (lon === null || lat === null) {
    return null;
  }
  const { endTime, hour } = parseEventTime(row.event_end_time);
  return {
    event_id: toNumber(row.event_id) ?? row.event_id,
    device_id: row.id ?? null,
    lon,
    lat,
    event_type: normalizeEventType(row.event_type),
    heading: toNumber(row.heading),
    max_g: toNumber(row["Max G"] ?? row.max_g),
    start_speed_mph: toNumber(row.start_speed),
    end_speed_mph: toNumber(row.ends_speed ?? row.end_speed),
    duration_s: toNumber(row.event_duration),
    end_time: endTime,
    hour
  };
}

export function parseEvents(csvText) {
  return parseCsv(csvText).map(normalizeEvent).filter(Boolean);
}
